import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { MOCK_WATCHLIST, type WatchlistEntry } from '@/data/aml/mockData';

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
}

export default function WatchlistSearchPage() {
	const [search, setSearch] = useState('');
	const [categoryFilter, setCategoryFilter] = useState('all');
	const [sourceFilter, setSourceFilter] = useState('all');
	const [page, setPage] = useState(1);
	const PAGE_SIZE = 20;

	const categories = useMemo(
		() => Array.from(new Set(MOCK_WATCHLIST.map((e) => e.category))),
		[],
	);
	const sources = useMemo(
		() => Array.from(new Set(MOCK_WATCHLIST.map((e) => e.source))),
		[],
	);

	const filtered = useMemo(() => {
		let result = MOCK_WATCHLIST;
		if (search) {
			const q = search.toLowerCase();
			result = result.filter(
				(e) =>
					e.name.toLowerCase().includes(q) || e.uid.toLowerCase().includes(q),
			);
		}
		if (categoryFilter !== 'all')
			result = result.filter((e) => e.category === categoryFilter);
		if (sourceFilter !== 'all')
			result = result.filter((e) => e.source === sourceFilter);
		return result;
	}, [search, categoryFilter, sourceFilter]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	return (
		<div className="min-h-screen bg-background">
			<div className="border-b border-border bg-white px-6 py-4">
				<h1 className="text-xl font-semibold text-[#0A0A0A]">
					Watchlist Search
				</h1>
				<p className="text-sm text-[#737373] mt-0.5">
					Search and browse the AML watchlist database
				</p>
			</div>

			<div className="p-6 space-y-4 max-w-[1200px]">
				<div className="flex flex-wrap items-center gap-3">
					<div className="relative flex-1 min-w-[240px] max-w-sm">
						<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
						<Input
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setPage(1);
							}}
							placeholder="Search by name or UID..."
							className="pl-9 h-9 shadow-sm"
						/>
					</div>
					<Select
						value={categoryFilter}
						onValueChange={(v) => {
							setCategoryFilter(v);
							setPage(1);
						}}
					>
						<SelectTrigger className="w-[160px] h-9 text-sm">
							<SelectValue placeholder="All Categories" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Categories</SelectItem>
							{categories.map((c) => (
								<SelectItem key={c} value={c}>
									{c}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select
						value={sourceFilter}
						onValueChange={(v) => {
							setSourceFilter(v);
							setPage(1);
						}}
					>
						<SelectTrigger className="w-[150px] h-9 text-sm">
							<SelectValue placeholder="All Sources" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Sources</SelectItem>
							{sources.map((s) => (
								<SelectItem key={s} value={s}>
									{s}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<span className="ml-auto text-sm text-[#737373]">
						{filtered.length} entries
					</span>
				</div>

				<Card className="border border-[#E5E5E5] overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="bg-[#FAFAFA]">
								<TableHead className="text-xs font-semibold text-[#737373]">
									UID
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Name
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Category
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Nationality
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									DOB
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									PEP Status
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Source
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Last Updated
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{paginated.map((entry: WatchlistEntry) => (
								<TableRow key={entry.id} className="hover:bg-[#FAFAFA]">
									<TableCell>
										<code className="text-xs text-[#525252]">{entry.uid}</code>
									</TableCell>
									<TableCell className="text-sm font-medium text-[#0A0A0A]">
										{entry.name}
									</TableCell>
									<TableCell>
										<span className="text-xs text-[#525252]">
											{entry.category}
										</span>
									</TableCell>
									<TableCell>
										<span className="text-xs text-[#525252]">
											{entry.nationality}
										</span>
									</TableCell>
									<TableCell>
										<span className="text-xs text-[#525252]">{entry.dob}</span>
									</TableCell>
									<TableCell>
										<Badge
											variant={
												entry.pep_status === 'PEP' ? 'destructive' : 'secondary'
											}
											className="text-xs"
										>
											{entry.pep_status}
										</Badge>
									</TableCell>
									<TableCell>
										<span className="text-xs text-[#525252]">
											{entry.source}
										</span>
									</TableCell>
									<TableCell>
										<span className="text-xs text-[#737373]">
											{formatDate(entry.last_updated)}
										</span>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Card>

				{totalPages > 1 && (
					<div className="flex items-center justify-end gap-2">
						<Button
							variant="outline"
							size="sm"
							disabled={page <= 1}
							onClick={() => setPage((p) => p - 1)}
						>
							Previous
						</Button>
						<span className="text-sm text-[#737373]">
							Page {page} of {totalPages}
						</span>
						<Button
							variant="outline"
							size="sm"
							disabled={page >= totalPages}
							onClick={() => setPage((p) => p + 1)}
						>
							Next
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}
